import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Loader2, MapPin, Clock, Trophy, Target, PenLine, Lock, Copy, Share2 } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import ConfidenceStars from "@/components/ConfidenceStars";
import DateNav, { toISODate } from "@/components/DateNav";
import { getVid } from "@/lib/vid";

const reunionNum = (r) => { const m = /(\d+)/.exec(r || ""); return m ? parseInt(m[1], 10) : 999; };
const courseNum = (c) => { const m = /(\d+)/.exec(c || ""); return m ? parseInt(m[1], 10) : 999; };
const heureToMin = (h) => { const m = /(\d{1,2})h(\d{2})?/.exec(h || ""); return m ? parseInt(m[1], 10) * 60 + parseInt(m[2] || "0", 10) : 9999; };
const heureSortKey = (h) => { const v = heureToMin(h); return v < 360 ? v + 1440 : v; };

function ReferralGate({ ref_ }) {
  const link = `${window.location.origin}/login?ref=${ref_.code}`;
  const shared = ref_.share_verified;
  const copy = () => { navigator.clipboard.writeText(link); toast.success("Lien copié !"); };
  const shareNow = async () => {
    const shareData = { title: "A Nous Turf", text: "Rejoins-moi sur A Nous Turf, les pronostics hippiques ! 🐴🍀", url: link };
    try {
      if (navigator.share) await navigator.share(shareData);
      else { await navigator.clipboard.writeText(link); toast.success("Lien copié !"); }
    } catch (e) {
      if (e && e.name !== "AbortError") console.warn("Partage impossible:", e);
    }
  };
  const blocked1000 = !ref_.within_1000;
  return (
    <div className="mb-8 rounded-2xl border border-[#FFD700]/30 bg-gradient-to-br from-[#00E676]/10 to-transparent p-6" data-testid="referral-gate">
      <div className="flex items-center gap-2 text-[#FFD700] font-black text-lg"><Lock size={20} /> Accès aux pronostics verrouillé</div>
      {blocked1000 ? (
        <p className="mt-2 text-[#A1B5A8]">Les 1000 premières places gratuites sont épuisées. Un abonnement d'accès arrivera bientôt. Restez connecté !</p>
      ) : (
        <>
          <p className="mt-2 text-[#E7F5EC]">Pour débloquer <b>tous les pronostics du jour</b>, deux conditions : jouez <b>15 jours</b> au Jeu <b>et</b> parrainez <b>2 joueurs</b>.</p>
          <div className="mt-4 flex flex-wrap items-center gap-2">
            <input readOnly value={link} data-testid="referral-link" className="flex-1 min-w-[220px] bg-[#0F1812] border border-[#00E676]/20 rounded-lg px-3 py-2 text-sm text-white" />
            <button onClick={shareNow} data-testid="referral-share-btn" className="px-4 py-2 rounded-lg font-bold text-[#04180a] bg-gradient-to-r from-[#00E676] to-[#39FF14] flex items-center gap-2"><Share2 size={16} /> Partager</button>
            <button onClick={copy} data-testid="referral-copy" className="px-4 py-2 rounded-lg font-semibold text-white bg-[#131F17] border border-[#00E676]/20 flex items-center gap-2"><Copy size={16} /> Copier</button>
          </div>
          <div className="mt-4 grid sm:grid-cols-2 gap-2">
            <div className={`rounded-lg border p-3 text-xs ${ref_.owner_days_ok ? "border-[#39FF14]/40 bg-[#00E676]/10 text-[#39FF14]" : "border-[#00E676]/10 bg-[#0F1812] text-[#A1B5A8]"}`} data-testid="own-days-status">
              <b>{ref_.owner_days_ok ? "✓" : "1."}</b> Jouer <b>{ref_.owner_game_days_needed || 15} jours</b> au Jeu <span className="font-bold">({Math.min(ref_.own_game_days || 0, ref_.owner_game_days_needed || 15)}/{ref_.owner_game_days_needed || 15})</span>
            </div>
            <div className={`rounded-lg border p-3 text-xs ${ref_.valid_count >= ref_.needed ? "border-[#39FF14]/40 bg-[#00E676]/10 text-[#39FF14]" : "border-[#00E676]/10 bg-[#0F1812] text-[#A1B5A8]"}`} data-testid="referrals-status">
              <b>{ref_.valid_count >= ref_.needed ? "✓" : "2."}</b> Parrainer <b>{ref_.needed} joueurs</b> validés <span className="font-bold">({ref_.valid_count}/{ref_.needed})</span>
            </div>
          </div>
          <div className="mt-3 rounded-lg bg-[#0F1812] border border-[#00E676]/10 p-3 text-xs text-[#A1B5A8]">
            <div className="font-bold text-[#39FF14] mb-1">Un filleul est validé quand :</div>
            <ul className="list-disc list-inside space-y-0.5">
              <li>Il participe <b>{ref_.filleul_game_days_needed || 5} jours</b> au Jeu</li>
              <li>Il partage son lien de parrainage (comme vous)</li>
            </ul>
          </div>
          <div className={`mt-3 text-xs font-bold ${shared ? "text-[#39FF14]" : "text-[#697C6F]"}`} data-testid="share-status">
            {shared ? "✓ Votre partage est vérifié" : "Partagez votre lien : la validation est automatique dès le premier clic reçu."}
          </div>
          <div className="mt-1 text-xs text-[#A1B5A8]" data-testid="referral-clicks">
            <b className="text-[#39FF14]">{ref_.clicks || 0}</b> clic(s) sur votre lien de parrainage
          </div>
        </>
      )}
    </div>
  );
}

function CourseCard({ course }) {
  return (
    <div className="ant-in rounded-xl border border-[#00E676]/15 bg-[#131F17] p-6 hover:border-[#00E676]/40 hover:-translate-y-1 transition-[transform,border-color] duration-300">
      <div className="flex items-start justify-between gap-3 mb-4">
        <div>
          <div className="flex items-center gap-2 text-[#39FF14] text-xs font-bold uppercase tracking-wider flex-wrap">
            <span className="px-2 py-0.5 rounded bg-[#00E676]/10">{course.reunion}</span>
            <span className="px-2 py-0.5 rounded bg-[#00E676]/10">{course.course}</span>
            {course.discipline && <span className="text-[#697C6F]">{course.discipline}</span>}
            {course.type_pari && <span className="px-2 py-0.5 rounded bg-[#39FF14] text-[#04180a]" data-testid="prono-type-pari">{course.type_pari}</span>}
          </div>
          <h3 className="mt-2 text-lg font-bold flex items-center gap-2">
            <MapPin size={16} className="text-[#00E676]" /> {course.hippodrome}
          </h3>
          {course.author_name && (
            <div className="mt-1 flex items-center gap-1.5 text-xs text-[#697C6F]" data-testid="prono-author">
              <PenLine size={12} className="text-[#39FF14]" /> Pronostic par <span className="text-[#A1B5A8] font-semibold">{course.author_name}</span>
            </div>
          )}
        </div>
        {course.heure && (
          <div className="flex items-center gap-1 text-[#A1B5A8] text-sm shrink-0">
            <Clock size={14} /> {course.heure}
          </div>
        )}
      </div>

      <div className="space-y-2">
        {(course.selections || []).map((s, i) => {
          return (
            <div key={i} className="rounded-2xl border border-[#00E676]/30 bg-[#c9f2d8] p-3">
              <div className="flex items-center gap-3 min-w-0">
                {s.casaque ? (
                  <span className="relative shrink-0">
                    <img src={s.casaque} alt="casaque" className="w-9 h-9 object-contain" />
                    <span className="absolute -bottom-1 -right-1 tabular text-[9px] leading-none px-1 py-0.5 rounded bg-[#0a7a3c] text-white font-black">{s.numero}</span>
                  </span>
                ) : (
                  <span className="tabular w-8 h-8 shrink-0 grid place-items-center rounded-md bg-[#0a7a3c] text-white font-black">
                    {s.numero}
                  </span>
                )}
                <div className="min-w-0">
                  <div className="font-bold truncate text-black">
                    {s.cheval}
                  </div>
                  {s.jockey && <div className="text-[11px] text-black/60 truncate">{s.jockey}</div>}
                  <ConfidenceStars value={s.etoiles} size={13} color="#E8A100" emptyColor="#00000022" />
                </div>
              </div>
              {s.commentaire && <p className="mt-2 text-sm text-black/70">{s.commentaire}</p>}
            </div>
          );
        })}
      </div>
      {(course.mon_ticket || course.commentaire) && (
        <div className="mt-4 space-y-2">
          {course.mon_ticket && (
            <div className="rounded-lg border border-[#39FF14]/25 bg-[#39FF14]/5 px-3 py-2 text-sm" data-testid="course-mon-ticket">
              <span className="text-[#39FF14] font-bold">Mon ticket : </span>
              <span className="text-[#E7F5EC]">{course.mon_ticket}</span>
            </div>
          )}
          {course.commentaire && (
            <p className="text-sm text-[#A1B5A8] italic" data-testid="course-commentaire">"{course.commentaire}"</p>
          )}
        </div>
      )}
    </div>
  );
}

export default function Dashboard() {
  const [selDate, setSelDate] = useState(toISODate());
  const [courses, setCourses] = useState([]);
  const [access, setAccess] = useState(true);
  const [ref, setRef] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selReunion, setSelReunion] = useState(null);

  const reunions = useMemo(() => {
    const map = {};
    for (const c of courses) {
      const r = c.reunion || "—";
      if (!map[r]) map[r] = { reunion: r, hippodrome: c.hippodrome, heures: [] };
      if (c.heure) map[r].heures.push(c.heure);
    }
    return Object.values(map)
      .map((x) => ({ ...x, firstHeure: [...x.heures].sort((a, b) => heureToMin(a) - heureToMin(b))[0] || "" }))
      .sort((a, b) => reunionNum(a.reunion) - reunionNum(b.reunion));
  }, [courses]);

  const displayed = useMemo(() => {
    const list = selReunion ? courses.filter((c) => c.reunion === selReunion) : courses;
    if (!selReunion) {
      return [...list].sort((a, b) => heureSortKey(a.heure) - heureSortKey(b.heure));
    }
    return [...list].sort((a, b) => {
      const rd = reunionNum(a.reunion) - reunionNum(b.reunion);
      return rd !== 0 ? rd : courseNum(a.course) - courseNum(b.course);
    });
  }, [courses, selReunion]);

  const load = async (date) => {
    setLoading(true);
    try {
      const res = await api.get("/courses", { params: date ? { date } : {} });
      const data = res.data.courses || res.data;
      setCourses(data);
      setAccess(res.data.access !== false);
      setRef(res.data.referral || null);
      if (res.data.referral && !res.data.referral.exempt) api.get("/referral/me", { params: { vid: getVid() } }).catch(() => {});
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load(selDate);
  }, []);

  const fmtDate = (d) =>
    d ? new Date(d).toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long" }) : "Aujourd'hui";

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-8">
        <div>
          <div className="text-[#39FF14] font-script text-2xl">Le mot de l'expert</div>
          <h1 className="text-4xl sm:text-5xl font-black">Pronostics du jour</h1>
          <p className="text-[#A1B5A8] mt-1 capitalize">{fmtDate(selDate)}</p>
        </div>
        <DateNav value={selDate} onChange={(d) => { setSelDate(d); setSelReunion(null); load(d); }} />
      </div>

      {!loading && access && reunions.length > 1 && (
        <div className="mb-6 flex flex-wrap items-center gap-2" data-testid="reunion-shortcuts">
          <button
            type="button"
            onClick={() => setSelReunion(null)}
            data-testid="reunion-chip-all"
            className={`text-xs font-bold px-3 py-1.5 rounded-full border transition-colors ${selReunion === null ? "bg-[#00E676] text-[#04180a] border-[#00E676]" : "bg-[#131F17] text-[#A1B5A8] border-[#00E676]/20 hover:border-[#00E676]/50"}`}
          >
            Toutes les réunions
          </button>
          {reunions.map((r) => (
            <button
              key={r.reunion}
              type="button"
              onClick={() => setSelReunion(r.reunion)}
              data-testid={`reunion-chip-${r.reunion}`}
              className={`text-xs font-bold px-3 py-1.5 rounded-full border transition-colors flex items-center gap-1.5 ${selReunion === r.reunion ? "bg-[#00E676] text-[#04180a] border-[#00E676]" : "bg-[#131F17] text-[#E7F5EC] border-[#00E676]/20 hover:border-[#00E676]/50"}`}
            >
              <span className={selReunion === r.reunion ? "text-[#04180a]" : "text-[#39FF14]"}>{r.reunion}</span>
              {r.hippodrome}
            </button>
          ))}
        </div>
      )}

      {!access && ref && <ReferralGate ref_={ref} />}

      {loading ? (
        <div className="flex justify-center py-24"><Loader2 className="w-8 h-8 animate-spin text-[#00E676]" /></div>
      ) : courses.length === 0 ? (
        <div className="text-center py-24 rounded-xl border border-dashed border-[#00E676]/20 bg-[#131F17]/50">
          <Target className="mx-auto text-[#2A3B30]" size={48} />
          <p className="mt-4 text-[#A1B5A8]">Aucun pronostic publié pour cette date.</p>
          <p className="text-sm text-[#697C6F]">Revenez plus tard, l'expert prépare ses sélections !</p>
        </div>
      ) : (
        <div className={`grid md:grid-cols-2 gap-5 ${!access ? "pointer-events-none select-none blur-[6px] opacity-70" : ""}`} data-testid="courses-grid">
          {displayed.map((c, i) => (
            <CourseCard key={c.id || i} course={c} />
          ))}
        </div>
      )}
    </div>
  );
}
